import { useMemo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/Card';
import { NumberInput } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { COMPONENT_SPECS, GROUP_SPECS, groupLabel, specOf } from '@/domain/catalog';
import { createComponent } from '@/domain/project';
import { asNonNegative } from '@/domain/values';
import { formatCompact } from '@/lib/format';
import type { ComponentInstance, ComponentKind, Project } from '@/domain/types';
import { ComponentFormModal } from './ComponentFormModal';

export interface ParamsPanelProps {
  project: Project;
  onUpdate: (updater: (project: Project) => Project) => void;
}

function summarize(component: ComponentInstance): { text: string; filled: number } {
  const spec = specOf(component.kind);
  const parts: string[] = [];
  let filled = 0;

  for (const field of spec.fields) {
    const value = component.fields[field.key];
    if (value === null || value === undefined || `${value}`.trim() === '') continue;
    filled += 1;
    if (parts.length >= 4) continue;
    const numeric = asNonNegative(value);
    const rendered = numeric === null ? `${value}` : formatCompact(numeric);
    parts.push(field.unit ? `${field.label} ${rendered} ${field.unit}` : `${field.label} ${rendered}`);
  }

  return { text: parts.join(' · '), filled };
}

/** 参数编辑区：安全系数 + 按分组排列的部件清单 */
export function ParamsPanel({ project, onUpdate }: ParamsPanelProps) {
  const [editing, setEditing] = useState<ComponentInstance | null>(null);
  const [adding, setAdding] = useState(false);

  const grouped = useMemo(
    () =>
      GROUP_SPECS.map((group) => ({
        group,
        items: project.components.filter((component) => specOf(component.kind).group === group.id),
      })).filter((entry) => entry.items.length > 0),
    [project.components],
  );

  const updateComponent = (next: ComponentInstance) => {
    onUpdate((current) => ({
      ...current,
      components: current.components.map((component) =>
        component.id === next.id ? next : component,
      ),
    }));
  };

  const removeComponent = (id: string) => {
    onUpdate((current) => ({
      ...current,
      components: current.components.filter((component) => component.id !== id),
    }));
  };

  const duplicateComponent = (id: string) => {
    onUpdate((current) => {
      const source = current.components.find((component) => component.id === id);
      if (!source) return current;
      const copy = createComponent(source.kind, {
        label: source.label,
        qty: source.qty,
        fields: { ...source.fields },
      });
      const index = current.components.findIndex((component) => component.id === id);
      const components = [...current.components];
      components.splice(index + 1, 0, copy);
      return { ...current, components };
    });
  };

  const addComponent = (kind: ComponentKind) => {
    onUpdate((current) => ({
      ...current,
      components: [...current.components, createComponent(kind)],
    }));
    setAdding(false);
  };

  const setReserve = (raw: string) => {
    const parsed = Number(raw.trim());
    const value = Number.isFinite(parsed) ? Math.min(90, Math.max(0, parsed)) : 20;
    onUpdate((current) => ({ ...current, reservePct: value }));
  };

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <CardHeader actions={<Badge tone="accent">默认 20%</Badge>}>
          <CardTitle subtitle="评估时只使用（100% − 预留）的推力，留出姿态控制与阵风余量">安全系数</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="max-w-xs">
            <NumberInput
              label="推力预留"
              unit="%"
              value={String(project.reservePct)}
              onChange={(event) => setReserve(event.target.value)}
              hint="修改后所有推重比与可行性结论会立即重算"
            />
          </div>
        </CardBody>
      </Card>

      {grouped.map(({ group, items }) => (
        <Card key={group.id}>
          <CardHeader actions={<Badge>{items.length} 件</Badge>}>
            <CardTitle subtitle={group.description}>{group.label}</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-2 p-0">
            {items.map((component) => {
              const spec = specOf(component.kind);
              const summary = summarize(component);
              return (
                <div
                  key={component.id}
                  className="flex flex-col gap-2 border-b border-line/60 px-3.5 py-2.5 last:border-b-0 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium text-fg">
                        {component.label?.trim() || spec.label}
                      </span>
                      <Badge>×{component.qty}</Badge>
                      {summary.filled === 0 ? <Badge tone="caution">未填参数</Badge> : null}
                    </div>
                    <p className="tabular mt-0.5 truncate text-[11px] text-fg-subtle">
                      {summary.filled === 0 ? '点「编辑」开始填写参数' : summary.text}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button size="md" onClick={() => setEditing(component)}>
                      编辑
                    </Button>
                    <Button size="md" variant="ghost" onClick={() => duplicateComponent(component.id)} title="复制该部件">
                      复制
                    </Button>
                    <Button
                      size="md"
                      variant="ghost"
                      onClick={() => removeComponent(component.id)}
                      title="删除该部件"
                      aria-label={`删除${component.label?.trim() || spec.label}`}
                    >
                      删除
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>
      ))}

      <Button size="lg" block variant="default" onClick={() => setAdding(true)}>
        + 添加部件
      </Button>

      <ComponentFormModal
        open={editing !== null}
        component={editing}
        onClose={() => setEditing(null)}
        onSave={updateComponent}
      />

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="添加部件"
        description="选一个类型加入清单，参数一样可以留空。"
        footer={
          <Button size="lg" variant="ghost" onClick={() => setAdding(false)}>
            取消
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {COMPONENT_SPECS.filter((spec) => spec.kind !== 'custom').map((spec) => (
            <Button
              key={spec.kind}
              size="lg"
              variant="default"
              block
              onClick={() => addComponent(spec.kind)}
              title={groupLabel(spec.group)}
            >
              {spec.label}
            </Button>
          ))}
          <Button size="lg" variant="default" block onClick={() => addComponent('custom')}>
            自定义部件
          </Button>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-fg-subtle">
          标准四轴建议保留 4 个电机 / 4 个电调 / 4 支桨；同轴双桨等特殊布局可以自行调整数量。
        </p>
      </Modal>
    </div>
  );
}
